/**
 * Bid reference-data admin (P3) — one tab per catalog plus the JSON import screen.
 *
 * All seven catalogs render from `catalogDescriptors.ts`, mirroring the backend's one-CatalogConfig-
 * per-catalog design.
 *
 * LAYOUT: deliberately the same furniture as BidSheetsPage — `mattersPage` >
 * `matters-content-container`, the shared `PageHeader` / `Tabs variant="pills"` / `SearchBar` /
 * `FilterDropdown` / `Table` / `CreateButton`. It previously hand-rolled its own tab strip, table
 * and buttons, which drifted from the rest of the app: an underline tab row where every other page
 * uses pills, unstyled row buttons that overflowed the table's right edge, and a bare "Show
 * deactivated" checkbox where the app filters with a dropdown. Reuse, not restyling — so the next
 * change to the shared table reaches this page too.
 *
 * Search + status filter + pagination are here because the data needs them: equipment alone is 137
 * rows out of the box, which is unusable as one unpaginated list.
 *
 * Writes are admin-only server-side (`require_permish({1})`); the route is gated to permish 1 too so
 * a standard user is not shown a screen where every button 403s.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faFileImport, faTriangleExclamation, faUserShield } from '@fortawesome/free-solid-svg-icons';
import PageHeader from '@frontend/Components/Titles/PageHeader/PageHeader';
import Tabs from '@frontend/Components/Tabs/Tabs';
import Table, { getDensityClass } from '@frontend/Components/Table/Table';
import SearchBar from '@frontend/Components/Inputs/SearchBar/SearchBar';
import FilterDropdown from '@frontend/Components/FilterDropdown';
import CreateButton from '@frontend/Components/CreateButton/CreateButton';
import ConfirmationModal from '@frontend/Components/ConfirmationModal/ConfirmationModal';
import ToastService from '@frontend/AppSettings/services/ToastService';
import { CATALOGS, CatalogDescriptor, CatalogField, catalogByKey } from './catalogDescriptors';
import CatalogFormModal from './CatalogFormModal';
import AuditPanel from './AuditPanel';
import TenantDefaultsPanel from './TenantDefaultsPanel';
import WorkAgreementsPanel from './WorkAgreementsPanel';
import AbsenceReasonsPanel from './AbsenceReasonsPanel';
import ImportPanel from './ImportPanel';
import RolesPanel from './RolesPanel';
import { useBidCapabilities } from '../useBidCapabilities';
import {
  CatalogItem,
  deactivateCatalogItem,
  deleteCatalogItem,
  fetchFleetRegistry,
  FleetRegistry,
  listCatalog,
  reactivateCatalogItem,
} from './referenceDataService';
import UnitsDrawer, { poolQuantity } from './UnitsDrawer';
import { invalidateCatalogOptions } from './useCatalogOptions';
import './BidAdminPage.css';

const IMPORT_TAB = 'import';
const ROLES_TAB = 'roles';
const AUDIT_TAB = 'audit';
// Tenant defaults (P6): a VIEW rather than a catalog tab, because it filters nothing — it shows
// the two settings the overtime classifier reads, which live on the tenant record, not in a catalog.
const DEFAULTS_TAB = 'defaults';
// P7a: the two surfaces 7b cannot enable without — an averaging agreement, and the vocabulary a
// supervisor picks from when a person's day is an absence.
const AGREEMENTS_TAB = 'agreements';
const ABSENCE_TAB = 'absence-reasons';

/** Render a raw API cell value for the list table. */
function cell(field: CatalogField, raw: unknown): React.ReactNode {
  if (raw === null || raw === undefined || raw === '') return '—';
  if (field.type === 'checkbox') return raw ? 'Yes' : 'No';
  if (field.type === 'json') return typeof raw === 'string' ? raw : JSON.stringify(raw);
  return String(raw);
}

const BidAdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // ASSIGN_ROLES is a separate capability from ADMIN_CATALOGS, so the tab appears only for callers
  // the API would actually let assign roles.
  const capabilities = useBidCapabilities();
  const tab = searchParams.get('tab') ?? CATALOGS[0].key;
  const catalog: CatalogDescriptor | undefined = catalogByKey(tab);

  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Replaces a bare "Show deactivated" checkbox: the app filters with dropdowns, and three states
  // (active / deactivated / all) are more useful than a two-way toggle when auditing a catalog.
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [editing, setEditing] = useState<CatalogItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CatalogItem | null>(null);
  /** Options for `catalogRef` form fields (currently only inventory -> equipment). */
  const [refOptions, setRefOptions] = useState<Record<string, Array<{ id: string; label: string }>>>({});

  // The fleet registry (Build U): fetched once per equipment-tab visit — the type rows'
  // unit counts and the Units drawer read the same response (the plan's cached-per-visit
  // rule). A failed fetch degrades the Fleet column to a muted dash, never an error wall.
  const [fleet, setFleet] = useState<FleetRegistry | null>(null);
  const [fleetFailed, setFleetFailed] = useState(false);
  const [unitsFor, setUnitsFor] = useState<CatalogItem | null>(null);
  const [addingUnitFor, setAddingUnitFor] = useState<CatalogItem | null>(null);

  /** Refetch the tab. Also drops the estimator's picker cache: without this a deactivation is
   *  invisible to an already-loaded estimator until a full page reload (module-level cache). */
  const refreshAfterMutation = useCallback((): void => {
    invalidateCatalogOptions();
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!catalog) return;
    setLoading(true);
    setError(null);
    try {
      setItems(await listCatalog(catalog.url));
    } catch (err: any) {
      setError(err?.message || `Could not load ${catalog.label}.`);
    } finally {
      setLoading(false);
    }
  }, [catalog]);

  useEffect(() => {
    void load();
  }, [load]);

  // Sequence-guarded (review fold): a slow FAILING fetch must not land over a later
  // success and dash out a column that holds good data.
  const fleetSeq = React.useRef(0);
  const loadFleet = useCallback(async (): Promise<void> => {
    const seq = ++fleetSeq.current;
    try {
      const data = await fetchFleetRegistry();
      if (seq === fleetSeq.current) {
        setFleet(data);
        setFleetFailed(false);
      }
    } catch {
      if (seq === fleetSeq.current) setFleetFailed(true);
    }
  }, []);

  useEffect(() => {
    if (catalog?.key !== 'equipment') return;
    void loadFleet();
  }, [catalog?.key, loadFleet]);

  // A tab change closes the fleet overlays (pre-PR review fold): a drawer surviving the
  // switch would build its add-unit ref options from the WRONG catalog's rows.
  useEffect(() => {
    setUnitsFor(null);
    setAddingUnitFor(null);
  }, [catalog?.key]);

  /** equipment_item_id → its tagged units / its untagged pool. Untyped units group under ''. */
  const unitsByType = useMemo(() => {
    const map = new Map<string, NonNullable<FleetRegistry['units']>>();
    (fleet?.units ?? []).forEach((u) => {
      const key = u.equipment_item_id ?? '';
      const list = map.get(key) ?? [];
      list.push(u);
      map.set(key, list);
    });
    return map;
  }, [fleet]);

  const poolByType = useMemo(() => {
    const map = new Map<string, FleetRegistry['untagged_pools'][number]>();
    (fleet?.untagged_pools ?? []).forEach((p) => {
      map.set(p.equipment_item_id ?? '', p);
    });
    return map;
  }, [fleet]);

  // A STABLE identity (review fold): an inline literal here is a new object every parent
  // render, and it feeds the modal's initial-state memo — a mid-typing capability refetch
  // would silently wipe the form back to the pre-filled type.
  const addUnitPrefill = useMemo(
    () => (addingUnitFor ? { equipment_item_id: addingUnitFor.id } : undefined),
    [addingUnitFor],
  );

  // Fetch the referenced catalogs this tab's form needs (active rows only — a retired item
  // should not be selectable on something new).
  useEffect(() => {
    const refs = (catalog?.fields ?? []).filter((f) => f.type === 'catalogRef' && f.refCatalog);
    if (!refs.length) return;
    let cancelled = false;
    void Promise.all(
      refs.map(async (f) => {
        const target = catalogByKey(f.refCatalog as string);
        if (!target) return [f.refCatalog as string, []] as const;
        const rows = await listCatalog(target.url, true).catch(() => [] as CatalogItem[]);
        return [
          f.refCatalog as string,
          rows.map((r) => ({ id: r.id, label: String(r[target.keyField] ?? r.id) })),
        ] as const;
      }),
    ).then((entries) => {
      if (!cancelled) setRefOptions(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [catalog]);

  /** Search matches the key column plus every column shown in the table — what the user can see is
   *  what they can search, so a hit is never invisible. */
  const visible = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const shown = (catalog?.fields ?? []).filter((f) => f.inTable);
    return items.filter((item) => {
      if (statusFilter === 'active' && !item.active) return false;
      if (statusFilter === 'inactive' && item.active) return false;
      if (!term) return true;
      const haystack = [item[catalog?.keyField ?? 'id'], ...shown.map((f) => item[f.name])]
        .map((v) => (v === null || v === undefined ? '' : String(v)).toLowerCase());
      return haystack.some((v) => v.includes(term));
    });
  }, [items, statusFilter, searchTerm, catalog]);

  const paged = useMemo(
    () => visible.slice((page - 1) * pageSize, page * pageSize),
    [visible, page, pageSize],
  );

  // Filtering to fewer pages than the current one would otherwise show an empty table.
  useEffect(() => {
    setPage(1);
  }, [tab, statusFilter, searchTerm, pageSize]);

  // ...and clamp when the LIST shrinks under us. Deleting or deactivating the last row of the last
  // page used to strand the user on a phantom page: the shared Table returns early on empty data, so
  // the pagination footer disappeared with the rows and there was no control left to get back.
  const pageLimit = Math.max(1, Math.ceil(visible.length / pageSize));
  useEffect(() => {
    if (page > pageLimit) setPage(pageLimit);
  }, [page, pageLimit]);

  const label = (item: CatalogItem): string =>
    String(item[catalog?.keyField ?? 'id'] ?? item.id);

  const handleDeactivate = async (item: CatalogItem): Promise<void> => {
    if (!catalog) return;
    try {
      if (item.active) {
        await deactivateCatalogItem(catalog.url, item.id);
        ToastService.success(`${label(item)} deactivated — existing bids keep their frozen rate`);
      } else {
        await reactivateCatalogItem(catalog.url, item.id);
        ToastService.success(`${label(item)} reactivated`);
      }
      refreshAfterMutation();
      await load();
    } catch (err: any) {
      ToastService.error(err?.message || 'Could not update that item.');
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!catalog || !pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteCatalogItem(catalog.url, target.id);
      ToastService.success(`${label(target)} deleted`);
      refreshAfterMutation();
      await load();
    } catch (err: any) {
      // 409 = still referenced by a bid. Say what to do instead, rather than "request failed".
      if (err?.status === 409) {
        ToastService.error(
          `${label(target)} is used by an existing bid and cannot be deleted. Deactivate it instead — that hides it from new bids while keeping history.`,
        );
      } else {
        ToastService.error(err?.message || 'Could not delete that item.');
      }
    }
  };

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (item: CatalogItem): void => {
    setEditing(item);
    setFormOpen(true);
  };

  const tableFields = (catalog?.fields ?? []).filter((f) => f.inTable);

  // CATALOGS ONLY. "Import JSON" is an action and "Roles" is access control — neither is a
  // reference-data category, and putting them here also meant "Labour roles" sat next to "Roles",
  // which are a pricing catalog and a permission system respectively. Both moved to header
  // actions; the seven catalogs then fit on one row, which removes the wrap this page had.
  const catalogTabs = CATALOGS.map((c) => ({ id: c.key, label: c.label }));

  /** Import / roles are full views, not tabs: they replace the catalog list rather than filtering it. */
  const view = tab === IMPORT_TAB ? IMPORT_TAB : tab === ROLES_TAB ? ROLES_TAB
    : tab === AUDIT_TAB ? AUDIT_TAB : tab === DEFAULTS_TAB ? DEFAULTS_TAB
    : tab === AGREEMENTS_TAB ? AGREEMENTS_TAB : tab === ABSENCE_TAB ? ABSENCE_TAB : null;
  const VIEW_META: Record<string, { title: string; subtitle: string }> = {
    [IMPORT_TAB]: {
      title: 'Import reference data',
      subtitle: 'Load a JSON file into a catalog — nothing is saved until you confirm',
    },
    [AUDIT_TAB]: {
      title: 'Audit log',
      subtitle: 'Every catalog and change-order mutation — who, when, and old \u2192 new',
    },
    [DEFAULTS_TAB]: {
      title: 'Tenant defaults',
      subtitle: 'The workweek start and default jurisdiction the overtime classifier reads',
    },
    [AGREEMENTS_TAB]: {
      title: 'Averaging agreements',
      subtitle: 'The legal instruments that let a rotation\u2019s hours average before overtime',
    },
    [ABSENCE_TAB]: {
      title: 'Absence reasons',
      subtitle: 'What a supervisor picks from when a person\u2019s day is an absence',
    },
    [ROLES_TAB]: {
      title: 'Bid access',
      subtitle: 'Who can see dollar values and edit bids — separate from the app permission level',
    },
  };

  const columns = catalog
    ? [
        {
          header: catalog.keyLabel,
          render: (item: CatalogItem) => (
            <span className={item.active ? '' : 'bid-admin-cell-inactive'}>{label(item)}</span>
          ),
        },
        ...tableFields.map((f) => ({
          header: f.label,
          render: (item: CatalogItem) => cell(f, item[f.name]),
        })),
        // The fleet column (Build U): each equipment TYPE discloses its ACTUAL units —
        // the count opens the Units drawer; untagged rows disclose as a pool quantity.
        ...(catalog.key === 'equipment'
          ? [{
            header: 'Fleet',
            render: (item: CatalogItem) => {
              if (fleetFailed || !fleet) {
                return <span className="bid-admin-cell-inactive">—</span>;
              }
              const count = unitsByType.get(item.id)?.length ?? 0;
              const pool = poolByType.get(item.id);
              return (
                <div className="bid-admin-fleet-cell">
                  <button
                    type="button"
                    className="bid-admin-btn"
                    // The type in the accessible name (review fold): "2 units" alone is
                    // identical across every row to a screen reader.
                    aria-label={`${count} unit${count === 1 ? '' : 's'} — ${label(item)}`}
                    onClick={() => setUnitsFor(item)}
                  >
                    {count} unit{count === 1 ? '' : 's'}
                  </button>
                  {pool && (
                    <span className="bid-admin-fleet-pool">
                      + {poolQuantity(pool)} untagged in pool
                    </span>
                  )}
                </div>
              );
            },
          }]
          : []),
        {
          header: 'Status',
          render: (item: CatalogItem) => (
            <span className={`bid-admin-badge ${item.active ? 'is-on' : 'is-off'}`}>
              {item.active ? 'Active' : 'Deactivated'}
            </span>
          ),
        },
        {
          // Visually-hidden rather than empty: the old markup had `<th aria-label="Actions" />` and
          // RolesPanel still does, so an empty string here left the page inconsistent with itself
          // and the column unnamed to a screen reader.
          header: <span className="bid-admin-sr-only">Actions</span>,
          render: (item: CatalogItem) => (
            <div className="bid-admin-row-actions">
              <button type="button" className="bid-admin-btn" onClick={() => openEdit(item)}>
                Edit
              </button>
              <button type="button" className="bid-admin-btn" onClick={() => handleDeactivate(item)}>
                {item.active ? 'Deactivate' : 'Reactivate'}
              </button>
              <button
                type="button"
                className="bid-admin-btn is-danger"
                onClick={() => setPendingDelete(item)}
              >
                Delete
              </button>
            </div>
          ),
        },
      ]
    : [];

  return (
    <div className="mattersPage bid-admin-page">
      {/* This screen is reached from a cog on the bids list and via /bids/admin directly, and it had
          no way back — leaving the browser button as the only exit. Matches BidDetailPage's own
          back control rather than inventing a third pattern. */}
      {view ? (
        <button
          className="bid-admin-back"
          onClick={() => setSearchParams({ tab: CATALOGS[0].key })}
          aria-label="Back to reference data"
        >
          <FontAwesomeIcon icon={faChevronLeft} /> Back to reference data
        </button>
      ) : (
        <button
          className="bid-admin-back"
          onClick={() => navigate('/bids')}
          aria-label="Back to bids"
        >
          <FontAwesomeIcon icon={faChevronLeft} /> Back to bids
        </button>
      )}

      <PageHeader
        title={view ? VIEW_META[view].title : 'Bid reference data'}
        subtitle={view ? VIEW_META[view].subtitle : 'Catalogs, rates and defaults that new bids are priced from'}
      >
        {catalog && (
          <div className="bids-header-actions">
            <button
              type="button"
              className="bids-walk-btn"
              onClick={() => setSearchParams({ tab: IMPORT_TAB })}
            >
              <FontAwesomeIcon icon={faFileImport} /> Import JSON
            </button>
            <button
              type="button"
              className="bids-walk-btn"
              onClick={() => setSearchParams({ tab: AUDIT_TAB })}
            >
              Audit log
            </button>
            <button
              type="button"
              className="bids-walk-btn"
              onClick={() => setSearchParams({ tab: DEFAULTS_TAB })}
            >
              Tenant defaults
            </button>
            <button
              type="button"
              className="bids-walk-btn"
              onClick={() => setSearchParams({ tab: AGREEMENTS_TAB })}
            >
              Averaging agreements
            </button>
            <button
              type="button"
              className="bids-walk-btn"
              onClick={() => setSearchParams({ tab: ABSENCE_TAB })}
            >
              Absence reasons
            </button>
            {/* Access control, kept visually apart from the pricing catalogs. Shown only to a caller
                the API would let assign roles (ASSIGN_ROLES is a separate capability from
                ADMIN_CATALOGS). */}
            {capabilities?.canAssignRoles && (
              <button
                type="button"
                className="bids-walk-btn"
                onClick={() => setSearchParams({ tab: ROLES_TAB })}
              >
                <FontAwesomeIcon icon={faUserShield} /> Manage bid access
              </button>
            )}
            <CreateButton
              onClick={openCreate}
              text={`New ${catalog.label.replace(/s$/, '')}`}
              showIcon
              aria-label={`New ${catalog.label} item`}
            />
          </div>
        )}
      </PageHeader>

      {/* Catalog-only. On a view (import / roles) there is nothing to tab between, search or filter,
          and rendering the strip with no pill selected made the tab bar claim the first catalog was
          active while a different screen was showing. */}
      {catalog && (
      <div className="matters-content-container">
        {/* The tab strip gets its OWN row. The bids list puts tabs, count and search on one line
            because it has three tabs; seven do not fit beside them at any width, and sharing the
            row is what made them wrap. One row of tabs, one row of controls. */}
        <div className="bid-admin-tabstrip">
          <Tabs
            tabs={catalogTabs}
            activeTabId={tab}
            onTabChange={(next) => setSearchParams({ tab: next })}
            variant="pills"
          />
        </div>

        <div className="matters-tabs-search-section">
          <div className="matters-tabs-section">
            {!loading && (
              <span className="matters-count">
                {/* paged of filtered, matching BidSheetsPage — `visible of items` claimed
                    "Showing 137 of 137" while 20 rows were on screen, and "Showing 5 of 5" over an
                    empty table after a mutation emptied the last page. */}
                Showing {paged.length} of {visible.length} {catalog.label.toLowerCase()}
              </span>
            )}
          </div>

          <div className="matters-search-section">
            <SearchBar
              onSearch={(term) => setSearchTerm(term || '')}
              placeholder={`Search ${catalog.label.toLowerCase()}...`}
              className="matters-search-bar"
            />
          </div>
        </div>

        <div className="matters-filters-row">
            <FilterDropdown
              label=""
              placeholder="All Statuses"
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Deactivated' },
              ]}
              value={statusFilter}
              onChange={(v) => setStatusFilter((Array.isArray(v) ? v[0] : v) as typeof statusFilter)}
            className="matters-filter-dropdown"
          />
        </div>
      </div>
      )}

      {tab === ROLES_TAB ? (
        capabilities?.canAssignRoles ? (
          <RolesPanel />
        ) : (
          <p className="bid-admin-empty">You do not have permission to assign bid roles.</p>
        )
      ) : tab === IMPORT_TAB ? (
        <ImportPanel onImported={refreshAfterMutation} />
      ) : tab === AUDIT_TAB ? (
        <AuditPanel />
      ) : tab === DEFAULTS_TAB ? (
        <TenantDefaultsPanel />
      ) : tab === AGREEMENTS_TAB ? (
        <WorkAgreementsPanel />
      ) : tab === ABSENCE_TAB ? (
        <AbsenceReasonsPanel />
      ) : !catalog ? (
        <p className="bid-admin-error">Unknown catalog.</p>
      ) : (
        <>
          {error && (
            <div className="bid-admin-error" role="alert">
              <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
            </div>
          )}

          {loading ? (
            <p className="bid-admin-empty">Loading {catalog.label}…</p>
          ) : (
            <div className={getDensityClass(pageSize)} data-testid="bid-admin-table">
              <Table
                columns={columns}
                data={paged}
                rowKey="id"
                emptyMessage={
                  items.length === 0
                    ? `No ${catalog.label.toLowerCase()} yet — add one, or load a set from the Import JSON tab.`
                    : `No ${catalog.label.toLowerCase()} match this search or filter.`
                }
                pagination={{
                  currPage: page,
                  pageLimit,
                  pageSize,
                  onPageChange: setPage,
                  onPageSizeChange: setPageSize,
                  totalItems: visible.length,
                  itemLabel: catalog.label.toLowerCase(),
                }}
              />
            </div>
          )}

          {formOpen && (
            <CatalogFormModal
              catalog={catalog}
              item={editing}
              isOpen={formOpen}
              onClose={() => {
                setFormOpen(false);
                setEditing(null);
              }}
              onSuccess={() => {
                refreshAfterMutation();
                void load();
              }}
              refOptions={refOptions}
            />
          )}

          {/* NEVER both (pre-PR review fold): the app's only stacked-FormModal pair would
              share one Escape keypress and one aria-labelledby id — the drawer hides while
              the add-unit form is up, and closing the form returns to it (state kept). */}
          {unitsFor && !addingUnitFor && (
            <UnitsDrawer
              typeName={String(unitsFor.name ?? '')}
              units={unitsByType.get(unitsFor.id) ?? []}
              pool={poolByType.get(unitsFor.id)}
              isOpen
              onClose={() => setUnitsFor(null)}
              onAddUnit={() => setAddingUnitFor(unitsFor)}
            />
          )}

          {addingUnitFor && (() => {
            const inventory = catalogByKey('inventory');
            if (!inventory) return null;
            return (
              <CatalogFormModal
                catalog={inventory}
                item={null}
                // The type comes pre-filled (the drawer's whole point); the equipment
                // options come from THIS tab's rows — we are standing on the catalog.
                // The prefilled type stays listed even when DEACTIVATED (review fold):
                // an active-only filter left the select LOOKING blank while the hidden
                // state still submitted the id — a retired type's fleet is still real,
                // so link it, disclosed.
                prefill={addUnitPrefill}
                refOptions={{
                  equipment: items
                    .filter((i) => i.active || i.id === addingUnitFor.id)
                    .map((i) => ({
                      id: i.id,
                      label: `${String(i.name ?? i.id)}${i.active ? '' : ' (deactivated)'}`,
                    })),
                }}
                isOpen
                onClose={() => setAddingUnitFor(null)}
                onSuccess={() => {
                  refreshAfterMutation();
                  void loadFleet();
                }}
              />
            );
          })()}

          <ConfirmationModal
            isOpen={pendingDelete !== null}
            onClose={() => setPendingDelete(null)}
            onConfirm={handleDelete}
            message={
              pendingDelete
                ? `Permanently delete "${label(pendingDelete)}"? If any bid already uses it, the delete is refused — deactivate it instead to keep that history.`
                : ''
            }
            confirmLabel="Delete"
          />
        </>
      )}
    </div>
  );
};

export default BidAdminPage;
